import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedDevice, clearSelectedDevice } from '../../store/slices/devicesSlice';
import { setShouldNavigateToSelectedDevice, setSidebarOpen, setSelectedCity, setSelectedState, setCityBoundary, clearCitySelection } from '../../store/slices/uiSlice';
import { getCityBoundary } from '../../utils/geospatial';
import DeviceList from '../DeviceList';
import ActiveFilters from '../ActiveFilters';
import AsyncSelect from 'react-select/async';
import { INDIAN_STATES } from '../../constants/indianStates';
import { MAJOR_INDIAN_CITIES } from '../../constants/majorIndianCities';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { canViewAdafruitLocations } from '../../store/slices/locationsSlice';

const useFirestoreEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' || process.env.REACT_APP_ENVIRONMENT === 'emulator';

const Sidebar = ({
  isSidebarOpen,
  onNotificationClick,
  activeFilters,
  onFilterChange,
  filterOptions,
  onRemoveFilter,
  showNameplates,
  onToggleNameplates,
  showGeofences,
  onToggleGeofences,
  isAdmin,
  onOpenAdminPanel,
  filteredDevices,
  onDeviceClick,
  isBoundaryLoading,
  selectedCity,
  selectedState,
  selectedCountry,
  setSelectedCountry,
  shouldNavigateToSelectedDevice,
  onLogout,
}) => {
  const dispatch = useDispatch();
  const selectedDevice = useSelector(state => state.devices.selectedDevice);
  const permissions = useSelector(state => state.auth.permissions);
  const includeEmptyLGBandsCategory = canViewAdafruitLocations(permissions);
  
  // removed country fetching as unified search does not expose country selector
  
  const loadStateOptions = async (inputValue) => {
    const q = (inputValue || '').trim();
    if (selectedCountry === 'in') {
      const base = INDIAN_STATES.map(s => ({ value: s, label: s }));
      if (!q) return base;
      const ql = q.toLowerCase();
      return base.filter(o => o.label.toLowerCase().includes(ql));
    }
    if (q.length < 2) return [];
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=10&countrycodes=${encodeURIComponent(selectedCountry || 'in')}&polygon_geojson=0&state=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      const seen = new Set();
      const options = [];
      data.forEach(item => {
        const name = (item.address && (item.address.state || item.display_name)) || item.display_name;
        if (!name) return;
        const normalized = name.toString();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        options.push({ value: normalized, label: normalized });
      });
      return options;
    } catch {
      return [];
    }
  };
  
  // Custom styles for react-select dropdowns with higher z-index
  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: 40,
      borderColor: state.isFocused ? '#9CA3AF' : '#E5E7EB',
      boxShadow: 'none',
      '&:hover': { borderColor: '#9CA3AF' },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px',
      minHeight: 40,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: 14,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1100,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1100,
    }),
  };

  // Stable wrappers to satisfy hook dependency rules
  const loadStateOptionsCb = React.useCallback(loadStateOptions, [selectedCountry]);

  // State declarations (must be before callbacks that use them)
  const [isLocating, setIsLocating] = useState(false);
  const [locateError, setLocateError] = useState('');
  const [lastSearchType, setLastSearchType] = useState(null);
  const [unifiedSelections, setUnifiedSelections] = useState([]);
  const [swimZones, setSwimZones] = useState([]);

  // Unified search default grouped options
  const defaultUnifiedOptions = React.useMemo(() => {
    const deviceDefaults = (Array.isArray(filteredDevices) ? filteredDevices.slice(0, 10) : []).map(d => ({
      value: d.device_id,
      label: d.displayName || d.name || d.device_id,
      type: 'device',
    }));
    const cityDefaults = selectedCountry === 'in'
      ? MAJOR_INDIAN_CITIES.slice(0, 10).map(c => ({ value: c, label: c, type: 'city' }))
      : [];
    const filterDefaults = Array.isArray(filterOptions)
      ? filterOptions.map(f => ({ value: f.value, label: f.label, type: 'filter' }))
      : [];

    const groups = [];
    if (filterDefaults.length) groups.push({ label: 'Assets', options: filterDefaults });
    if (deviceDefaults.length) groups.push({ label: 'Devices', options: deviceDefaults });
    if (cityDefaults.length) groups.push({ label: 'Region', options: cityDefaults });
    return groups.length ? groups : deviceDefaults;
  }, [filteredDevices, selectedCountry, filterOptions]);

  // Unified search loader combining devices, cities, states, and swimzones
  const loadUnifiedOptions = React.useCallback(async (inputValue) => {
    const q = (inputValue || '').trim();

    // Devices (local)
    const deviceBase = Array.isArray(filteredDevices) ? filteredDevices : [];
    const deviceMatches = (!q
      ? deviceBase.slice(0, 25)
      : deviceBase.filter(d => {
          const name = (d.displayName || d.name || '').toLowerCase();
          const id = (d.device_id || '').toLowerCase();
          return name.includes(q.toLowerCase()) || id.includes(q.toLowerCase());
        }).slice(0, 50)
    ).map(d => ({ value: d.device_id, label: d.displayName || d.name || d.device_id, type: 'device' }));

    // Swimzones (local)
    const swimZoneBase = Array.isArray(swimZones) ? swimZones : [];
    const swimZoneMatches = (!q
      ? swimZoneBase.slice(0, 25)
      : swimZoneBase.filter(z => {
          const beachName = (z.beach_name || '').toLowerCase();
          const pointName = (z.point_name || '').toLowerCase();
          const searchText = `${beachName} ${pointName}`.trim();
          return searchText.includes(q.toLowerCase());
        }).slice(0, 50)
    ).map(z => ({
      value: z.id,
      label: z.beach_name 
        ? (z.point_name ? `${z.beach_name} - ${z.point_name}` : z.beach_name)
        : (z.point_name || 'Swimzone'),
      type: 'swimzone',
      zone: z // Store full zone data for navigation
    }));

    // Cities / States (remote or static)
    let cityMatches = [];
    let stateMatches = [];
    let filterMatches = [];

    // Filters (always include, even when no query) – default group should be Assets
    try {
      const baseFilters = Array.isArray(filterOptions) ? filterOptions : [];
      filterMatches = (!q
        ? baseFilters
        : baseFilters.filter(f => (f.label || '').toLowerCase().includes(q.toLowerCase()))
      ).map(f => ({ value: f.value, label: f.label, type: 'filter' }));
    } catch { filterMatches = []; }

    if (q.length >= 2) {
      // Region (Static list only)
      try {
        cityMatches = MAJOR_INDIAN_CITIES
          .filter(c => c.toLowerCase().includes(q.toLowerCase()))
          .map(c => ({ value: c, label: c, type: 'city' }));
      } catch { cityMatches = []; }

      // States
      try {
        if (selectedCountry === 'in') {
          const base = INDIAN_STATES.map(s => ({ value: s, label: s }));
          stateMatches = base
            .filter(o => o.label.toLowerCase().includes(q.toLowerCase()))
            .slice(0, 10)
            .map(o => ({ ...o, type: 'state' }));
        } else {
          const stateOpts = await loadStateOptionsCb(q);
          stateMatches = (stateOpts || []).slice(0, 10).map(o => ({ ...o, type: 'state' }));
        }
      } catch { stateMatches = []; }

      // (filterMatches already computed above for both empty and non-empty q)
    }

    const groups = [];
    if (filterMatches.length) groups.push({ label: 'Assets', options: filterMatches });
    if (deviceMatches.length) groups.push({ label: 'Devices', options: deviceMatches });
    if (swimZoneMatches.length) groups.push({ label: 'Swimzones', options: swimZoneMatches });
    if (cityMatches.length) groups.push({ label: 'Region', options: cityMatches });
    if (stateMatches.length) groups.push({ label: 'States', options: stateMatches });

    // Fallback to flat list if nothing grouped
    return groups.length ? groups : deviceMatches;
  }, [filteredDevices, swimZones, selectedCountry, filterOptions, loadStateOptionsCb]);

  useEffect(() => {
    const getIstDateStr = (value) => {
      if (!value) return null;
      const date = value instanceof Date
        ? value
        : value.seconds !== undefined
          ? new Date(value.seconds * 1000)
          : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      const istString = date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
      const istDate = new Date(istString);
      const day = String(istDate.getDate()).padStart(2, '0');
      const month = String(istDate.getMonth() + 1).padStart(2, '0');
      const year = istDate.getFullYear();
      return `${year}-${month}-${day}`;
    };

    const now = new Date();
    const istString = now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    const istDate = new Date(istString);
    const day = String(istDate.getDate()).padStart(2, '0');
    const monthNumber = String(istDate.getMonth() + 1).padStart(2, '0');
    const year = istDate.getFullYear();
    const todayDocId = `${day}-${monthNumber}-${year}`;
    const todayDateStr = `${year}-${monthNumber}-${day}`;

    const entriesRef = collection(db, 'swimzone', todayDocId, 'entries');

    const processSnapshot = (snapshot) => {
      const zones = [];
      snapshot.forEach((entryDoc) => {
        const entry = entryDoc.data();
        const entryDateStr = entry.date_str || getIstDateStr(entry.report_datetime || entry.created_at);
        if (entryDateStr && entryDateStr !== todayDateStr) return;
        zones.push({ id: entryDoc.id, ...entry });
      });
      setSwimZones(zones);
    };

    if (useFirestoreEmulator) {
      const poll = async () => {
        try {
          const snapshot = await getDocs(entriesRef);
          processSnapshot(snapshot);
        } catch (err) {
          console.error('[Sidebar] Swim zones poll error:', err);
          setSwimZones([]);
        }
      };
      poll();
      const intervalId = setInterval(poll, 5000);
      return () => clearInterval(intervalId);
    }

    const unsubscribe = onSnapshot(entriesRef, processSnapshot, (error) => {
      console.error('[Sidebar] Error fetching swim zones:', error);
      setSwimZones([]);
    });

    return () => unsubscribe();
  }, []);

  // Toggle body class for mobile map control offset when sidebar is open
  useEffect(() => {
    const cls = 'sidebar-open';
    if (isSidebarOpen) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isSidebarOpen]);

  // Log currently filtered devices' coordinates whenever the list updates
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && Array.isArray(filteredDevices) && filteredDevices.length > 0) {
      console.log(`[Sidebar] Filtered devices updated. Count: ${filteredDevices.length}`);
    }
  }, [filteredDevices]);

  // Check if selected device has valid coordinates when it changes
  useEffect(() => {
    if (selectedDevice && isLocating && lastSearchType === 'device') {
      const lat = typeof selectedDevice.last_latitude === 'string' ? parseFloat(selectedDevice.last_latitude) : selectedDevice.last_latitude;
      const lng = typeof selectedDevice.last_longitude === 'string' ? parseFloat(selectedDevice.last_longitude) : selectedDevice.last_longitude;
      
      // Check if coordinates are valid (not null, not NaN, not 0, and within valid ranges)
      const hasValidCoords = lat != null && lng != null && 
                            !isNaN(lat) && !isNaN(lng) &&
                            lat !== 0 && lng !== 0 &&
                            lat >= -90 && lat <= 90 &&
                            lng >= -180 && lng <= 180;
      
      // Only show error if we're still locating and coordinates are invalid
      // If shouldNavigateToSelectedDevice is true, navigation is happening, so don't show error
      if (!hasValidCoords && !shouldNavigateToSelectedDevice) {
        // Wait a bit to see if navigation flag gets set
        const checkTimer = setTimeout(() => {
          if (!shouldNavigateToSelectedDevice) {
            setLocateError('Location unavailable for this device');
          }
        }, 500);
        return () => clearTimeout(checkTimer);
      } else if (hasValidCoords || shouldNavigateToSelectedDevice) {
        setLocateError('');
      }
    }
  }, [selectedDevice, isLocating, lastSearchType, shouldNavigateToSelectedDevice]);

  // Shared handler for both mobile and desktop AsyncSelect — single source of truth
  const handleUnifiedSearchChange = React.useCallback((selections) => {
    const nextSelections = selections || [];

    const filterItems = nextSelections.filter(s => s.type === 'filter');
    const lastDevice = [...nextSelections].reverse().find(s => s.type === 'device');
    const lastSwimZone = [...nextSelections].reverse().find(s => s.type === 'swimzone');
    const lastCity = [...nextSelections].reverse().find(s => s.type === 'city');
    const lastState = [...nextSelections].reverse().find(s => s.type === 'state');

    let geoItem = null;
    if (lastCity && lastState) {
      const lastIndexCity = nextSelections.lastIndexOf(lastCity);
      const lastIndexState = nextSelections.lastIndexOf(lastState);
      geoItem = lastIndexCity > lastIndexState ? lastCity : lastState;
    } else {
      geoItem = lastCity || lastState || null;
    }

    const normalizedSelections = [
      ...filterItems,
      ...(lastDevice ? [lastDevice] : []),
      ...(lastSwimZone ? [lastSwimZone] : []),
      ...(geoItem ? [geoItem] : []),
    ];

    setUnifiedSelections(normalizedSelections);

    const selectedFilters = filterItems.map(s => s.value);
    if (selectedFilters.length === 0) {
      onFilterChange('all');
    } else {
      onFilterChange(selectedFilters);
    }

    if (geoItem?.type === 'city') {
      const cityName = geoItem.value || '';
      dispatch(setSelectedCity(cityName));
      dispatch(setSelectedState(''));
      setLastSearchType('city');
      dispatch(clearSelectedDevice());
      dispatch(setShouldNavigateToSelectedDevice(false));
      getCityBoundary(cityName, '', selectedCountry).then(boundary => {
        if (boundary) dispatch(setCityBoundary(boundary));
      }).catch(err => { console.error('Error fetching city boundary:', err); });
    } else if (geoItem?.type === 'state') {
      const stateName = geoItem.value || '';
      dispatch(setSelectedState(stateName));
      dispatch(setSelectedCity(''));
      setLastSearchType('state');
      dispatch(clearSelectedDevice());
      dispatch(setShouldNavigateToSelectedDevice(false));
      getCityBoundary('', stateName, selectedCountry).then(boundary => {
        if (boundary) dispatch(setCityBoundary(boundary));
      }).catch(err => { console.error('Error fetching state boundary:', err); });
    } else {
      dispatch(clearCitySelection());
    }

    if (lastDevice) {
      setLastSearchType('device');
      const device = (Array.isArray(filteredDevices) ? filteredDevices : []).find(d => d.device_id === lastDevice.value);
      if (device) {
        setIsLocating(true);
        setLocateError('');
        onDeviceClick(device);
        const t = setTimeout(() => { setIsLocating(false); }, 2000);
        const check = setInterval(() => {
          if (shouldNavigateToSelectedDevice) {
            setIsLocating(false);
            setLocateError('');
            clearInterval(check);
            clearTimeout(t);
          }
        }, 100);
      }
    } else if (lastSwimZone) {
      setLastSearchType('swimzone');
      const zone = lastSwimZone.zone || (Array.isArray(swimZones) ? swimZones.find(z => z.id === lastSwimZone.value) : null);
      if (zone) {
        setIsLocating(true);
        setLocateError('');
        dispatch(clearSelectedDevice());
        dispatch(setShouldNavigateToSelectedDevice(false));
        let lat = zone.lat || zone.latitude;
        let lng = zone.lng || zone.longitude;
        if (!lat || !lng) {
          const geoField = zone.co_ordinates || zone.coordinates;
          if (geoField) {
            if (geoField.latitude !== undefined) {
              lat = geoField.latitude;
              lng = geoField.longitude;
            } else if (Array.isArray(geoField) && geoField.length >= 2) {
              lat = geoField[0];
              lng = geoField[1];
            }
          }
        }
        if (typeof lat === 'string') lat = parseFloat(lat);
        if (typeof lng === 'string') lng = parseFloat(lng);
        if (lat && lng && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          const tempDevice = {
            device_id: `swimzone-${zone.id}`,
            displayName: zone.beach_name || zone.point_name || 'Swimzone',
            last_latitude: lat,
            last_longitude: lng,
            device_type: 'swimzone',
            status: 'active'
          };
          onDeviceClick(tempDevice);
          setIsLocating(false);
        } else {
          setLocateError('Location unavailable for this swimzone');
          setIsLocating(false);
        }
      }
    } else if (normalizedSelections.length === 0 && (lastSearchType === 'device' || lastSearchType === 'swimzone')) {
      setLocateError('');
      setIsLocating(true);
      const defaultCenter = [15.4989, 73.8278];
      dispatch(setSelectedDevice({ device_id: '__default__', last_latitude: defaultCenter[0], last_longitude: defaultCenter[1] }));
      dispatch(setShouldNavigateToSelectedDevice(true));
      setTimeout(() => setIsLocating(false), 1000);
      setLastSearchType(null);
    }
  }, [filteredDevices, swimZones, selectedCountry, onFilterChange, onDeviceClick, shouldNavigateToSelectedDevice, lastSearchType, dispatch]);

  return (
    <div
      className={`bg-white ${isSidebarOpen ? 'px-4 pb-16 pt-16 lg:p-5 lg:pt-5' : ''} shadow-lg flex flex-col gap-3 z-[1000] ${isSidebarOpen ? 'overflow-visible' : 'overflow-hidden'} lg:overflow-hidden transition-transform duration-300 ease-in-out
      fixed left-0 top-0 bottom-0 ${isSidebarOpen ? 'translate-x-0 w-[80vw]' : '-translate-x-full w-0'}
      lg:static lg:translate-x-0 md:w-[340px] lg:w-[400px] xl:w-[500px] pl-3 pr-3`}
    >
      {/* Mobile/Tablet: Back button and Global Search on same line */}
      <div className="lg:hidden flex items-center gap-2 pt-3 px-3 pb-3 border-b border-gray-200">
        {/* Mobile close arrow - improved styling */}
        <button
          type="button"
          aria-label="Close sidebar"
          title="Close sidebar"
          onClick={() => dispatch(setSidebarOpen(false))}
          className="relative w-fit z-[1100] bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm hover:bg-gray-50 hover:shadow-md hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-200 flex items-center justify-center group flex-shrink-0"
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path 
              d="M15 18l-6-6 6-6" 
              stroke="currentColor"
            />
          </svg>
        </button>
        {/* Unified search for devices, cities, and states - Mobile/Tablet */}
        <div className="flex-1 border border-gray-200 rounded-lg bg-white">
          <AsyncSelect
            cacheOptions
            defaultOptions={defaultUnifiedOptions}
            loadOptions={loadUnifiedOptions}
            isMulti
            isClearable
            isSearchable
            value={unifiedSelections}
            placeholder="Global Search"
            aria-label="Global search for assets, devices, swimzones, cities, and states"
            title="Global Search"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            components={{ IndicatorSeparator: () => null }}
            onChange={handleUnifiedSearchChange}
            noOptionsMessage={() => 'Type to search assets, devices, swimzones, cities, or states'}
            styles={selectStyles}
          />
          {isLocating && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
              Locating device...
            </div>
          )}
          {!isLocating && locateError && (
            <div className="mt-2 text-xs text-red-600">{locateError}</div>
          )}
        </div>
      </div>

      {/* Desktop: Back button section */}
      <div className="hidden lg:block header-left relative border-b border-gray-200 lg:border-b-0 pt-3 px-3 pb-3 lg:pt-0 lg:px-0 lg:pb-0">
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-3">
        <div className="filter-controls flex flex-col gap-3">
        
        {/* Unified search for devices, cities, and states - Desktop */}
        <div className="hidden lg:block border border-gray-200 rounded-lg bg-white lg:px-4 lg:py-3">
          <div className="w-full">
            <AsyncSelect
              cacheOptions
              defaultOptions={defaultUnifiedOptions}
              loadOptions={loadUnifiedOptions}
              isMulti
              isClearable
              isSearchable
              value={unifiedSelections}
              placeholder="Global Search"
              aria-label="Global search for assets, devices, swimzones, cities, and states"
              title="Global Search"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition="fixed"
              components={{ IndicatorSeparator: () => null }}
              onChange={handleUnifiedSearchChange}
              noOptionsMessage={() => 'Type to search assets, devices, swimzones, cities, or states'}
              styles={selectStyles}
            />
            {isLocating && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                Locating device...
              </div>
            )}
            {!isLocating && locateError && (
              <div className="mt-2 text-xs text-red-600">{locateError}</div>
            )}
          </div>
        </div>
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={onRemoveFilter}
          options={filterOptions}
        />
        {/* Mobile toggles: stacked full-width toggle buttons */}
        <div className="lg:hidden flex flex-col gap-2 px-0 mt-2">
          {/* Device Names Toggle Button */}
          <div
            role="switch"
            aria-checked={!!showNameplates}
            tabIndex={0}
            onClick={onToggleNameplates}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleNameplates(); } }}
            className="relative w-full h-12 rounded-lg cursor-pointer select-none transition-all duration-300 px-4 flex items-center justify-between bg-gray-200 text-gray-900 hover:bg-gray-300 shadow-sm"
            title="Toggle device names"
          >
            <span className="text-sm font-semibold">Device Names</span>
            <div className="flex items-center gap-2 transition-all duration-300">
              <span className={`text-xs font-medium ${showNameplates ? 'text-blue-600' : 'text-gray-600'}`}>
                {showNameplates ? 'ON' : 'OFF'}
              </span>
              <div className={`w-10 h-6 rounded-full relative transition-all duration-300 ${
                showNameplates ? 'bg-blue-600' : 'bg-gray-400'
              }`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ${
                  showNameplates ? 'right-1' : 'left-1'
                }`}></div>
              </div>
            </div>
          </div>

          {/* Geofence Region Toggle Button */}
          <div
            role="switch"
            aria-checked={!!showGeofences}
            tabIndex={0}
            onClick={onToggleGeofences}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleGeofences(); } }}
            className="relative w-full h-12 rounded-lg cursor-pointer select-none transition-all duration-300 px-4 flex items-center justify-between bg-gray-200 text-gray-900 hover:bg-gray-300 shadow-sm"
            title="Toggle geofence region"
          >
            <span className="text-sm font-semibold">Geofence Region</span>
            <div className="flex items-center gap-2 transition-all duration-300">
              <span className={`text-xs font-medium ${showGeofences ? 'text-blue-600' : 'text-gray-600'}`}>
                {showGeofences ? 'ON' : 'OFF'}
              </span>
              <div className={`w-10 h-6 rounded-full relative transition-all duration-300 ${
                showGeofences ? 'bg-blue-600' : 'bg-gray-400'
              }`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ${
                  showGeofences ? 'right-1' : 'left-1'
                }`}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop toggles: side-by-side layout */}
        <div className="hidden lg:flex gap-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 my-1 justify-between">
          {/* Device Names Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Device Names</span>
            <label className="relative inline-block w-[48px] h-6 flex-shrink-0">
              <input
                type="checkbox"
                checked={showNameplates}
                onChange={onToggleNameplates}
                className="opacity-0 w-0 h-0"
              />
              <span className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 rounded-full transition-all duration-400 before:absolute before:h-4 before:w-4 before:left-1 before:bottom-1 before:bg-white before:rounded-full before:transition-all before:duration-400 ${showNameplates ? 'bg-blue-500 before:translate-x-6' : 'bg-gray-300'}`}></span>
            </label>
          </div>
          
          {/* Geofence Region Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Geofence Region</span>
            <label className="relative inline-block w-[48px] h-6 flex-shrink-0">
              <input
                type="checkbox"
                checked={showGeofences}
                onChange={onToggleGeofences}
                className="opacity-0 w-0 h-0"
              />
              <span className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 rounded-full transition-all duration-400 before:absolute before:h-4 before:w-4 before:left-1 before:bottom-1 before:bg-white before:rounded-full before:transition-all before:duration-400 ${showGeofences ? 'bg-blue-500 before:translate-x-6' : 'bg-gray-300'}`}></span>
            </label>
          </div>
        </div>

        {isAdmin && (
          <div className="my-2 px-0">
            <button
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white border-none py-3 px-4 rounded-lg text-sm font-semibold cursor-pointer shadow-md transition-all duration-200 flex items-center justify-center gap-2 hover:from-blue-700 hover:to-blue-800 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-sm"
              onClick={onOpenAdminPanel}
              title="Admin Command Control Panel"
            >
              🔐 Admin Panel
            </button>
          </div>
        )}
      </div>
          <DeviceList
            devices={filteredDevices}
            selectedDeviceId={selectedDevice?.device_id}
            onDeviceClick={onDeviceClick}
            activeFilters={activeFilters}
            swimZones={swimZones}
            includeEmptyLGBandsCategory={includeEmptyLGBandsCategory}
          />
        </div>
    </div>
  );
};

export default Sidebar;
